import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Landmark } from 'lucide-react';
import type { TaxOfficeToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useTaxOfficesList, useTaxOfficeCountries, useTaxOfficeMutations } from '~/hooks/use-tax-offices';
import { tokenToForm, type TaxOfficeFormValues } from '~/lib/schemas/tax-office';
import { TaxOfficeEditDialog } from './tax-office-edit-dialog';

/** Tax offices (ΔΟΥ) CRUD surface — parity with the legacy admin grid (Code/Country/Description/Address/City/Postal/Visible/Sort). */
export function TaxOfficesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useTaxOfficesList();
	const { data: countries = [] } = useTaxOfficeCountries();
	const { remove } = useTaxOfficeMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<TaxOfficeFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<TaxOfficeToken | null>(null);

	const countryMap = useMemo(() => new Map(countries.map(c => [c.id, c.nm])), [countries]);
	const countryOf = (o: TaxOfficeToken) => (o.cid == null ? '' : (countryMap.get(o.cid) ?? ''));

	const columns = useMemo<SystemGridColumn<TaxOfficeToken>[]>(
		() => [
			{
				key: 'code',
				header: t('system.taxOffices.columns.code'),
				width: '120px',
				sortValue: o => o.cd,
				exportValue: o => o.cd,
				render: o => <span className="truncate font-mono text-xs font-medium text-foreground">{o.cd}</span>
			},
			{
				key: 'description',
				header: t('system.taxOffices.columns.description'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.dsc ?? '',
				exportValue: o => o.dsc ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.dsc ?? ''}>
						{o.dsc || '—'}
					</span>
				)
			},
			{
				key: 'address',
				header: t('system.taxOffices.columns.address'),
				width: '200px',
				sortValue: o => o.adr ?? '',
				exportValue: o => o.adr ?? '',
				render: o => (
					<span className="truncate text-sm text-foreground-muted" title={o.adr ?? ''}>
						{o.adr || '—'}
					</span>
				)
			},
			{
				key: 'city',
				header: t('system.taxOffices.columns.city'),
				width: '120px',
				sortValue: o => o.ct ?? '',
				exportValue: o => o.ct ?? '',
				render: o => <span className="truncate text-sm text-foreground-muted">{o.ct || '—'}</span>
			},
			{
				key: 'postalCode',
				header: t('system.taxOffices.columns.postalCode'),
				width: '100px',
				sortValue: o => o.pc ?? '',
				exportValue: o => o.pc ?? '',
				render: o => <span className="font-mono text-xs text-foreground-muted">{o.pc || '—'}</span>
			},
			{
				key: 'country',
				header: t('system.taxOffices.columns.country'),
				width: '140px',
				sortValue: o => countryOf(o),
				exportValue: o => countryOf(o),
				render: o => <span className="truncate text-sm text-foreground-muted">{countryOf(o) || '—'}</span>,
				toggleable: true
			},
			{
				key: 'phone',
				header: t('system.fields.phone'),
				width: '130px',
				defaultVisible: false,
				sortValue: o => o.tel ?? '',
				exportValue: o => o.tel ?? '',
				render: o => <span className="font-mono text-xs text-foreground-muted">{o.tel || '—'}</span>
			},
			{
				key: 'erpCode',
				header: t('system.fields.erpCode'),
				width: '120px',
				defaultVisible: false,
				sortValue: o => o.erpcd ?? '',
				exportValue: o => o.erpcd ?? '',
				render: o => <span className="font-mono text-xs text-foreground-muted">{o.erpcd || '—'}</span>
			},
			{
				key: 'visible',
				header: t('system.taxOffices.columns.visible'),
				width: '80px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => (o.iv ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'sort',
				header: t('system.taxOffices.columns.sort'),
				width: '70px',
				align: 'right',
				sortValue: o => o.si ?? 0,
				exportValue: o => o.si ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.si}</span>
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, countryMap]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<TaxOfficeToken>
				gridId="tax-offices"
				title={t('system.taxOffices.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.cd}
				columns={columns}
				searchPredicate={(o, q) => `${o.cd ?? ''} ${o.dsc ?? ''} ${o.adr ?? ''} ${o.ct ?? ''} ${o.pc ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'code', dir: 'asc' }}
				emptyIcon={<Landmark size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('system.taxOffices.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="tax-offices"
			/>

			<TaxOfficeEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} countries={countries} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('system.taxOffices.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('system.taxOffices.deleteBody', { name: deleteRow?.dsc || deleteRow?.cd || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
